import { useEffect, useRef, useState } from 'react'
import type { AlertThresholds, BackOfficeSettings, BookingSettings, FailedKoalendarDelivery, KeyedLabel, KoalendarPage } from '@fitting-room/contracts'
import { useAppointmentTypes, useBackOffice, useFailedKoalendar, useRetryFailedKoalendar, useSaveBackOffice } from '../../../lib/adminQueries'
import { formatWallTime } from '../../../lib/appointments'
import { boutiqueDateTime } from '../../../lib/boutique'
import { bpsFromPercent, keyForLabel, percentFromBps, percentList, timeList } from '../../../lib/backOffice'
import { useStaffToken } from '../../../lib/staffToken'

/** The console's own gold, offered as the starting point for a type with no color yet. */
const DEFAULT_SWATCH = '#b08d57'

/** The alert thresholds, in the order and words of `docs/22-back-office.md` § Alerts. */
const THRESHOLDS: ReadonlyArray<readonly [keyof AlertThresholds, string, string]> = [
  ['supplierOrderDays', 'Not ordered from the supplier', 'days after the order, for a made-to-order line'],
  ['noEtaDays', 'Ordered, no ETA', 'days after it was ordered'],
  ['etaBufferDays', 'ETA too close to the wedding', 'days before the wedding date, at the latest'],
  ['arrivedNotPickedUpDays', 'Arrived, not picked up', 'days after it arrived'],
  ['weddingSoonDays', 'Wedding coming up, not picked up', 'days before the wedding'],
  ['offRackHoldDays', 'Off the rack on hold', 'days on hold'],
]

interface Draft {
  bookingSources: KeyedLabel[]
  noPurchaseReasons: KeyedLabel[]
  tierColors: Record<string, string>
  /** The types no longer offered. */
  retiredTiers: string[]
  salesTax: string
  presets: string
  maxDiscount: string
  orderPrefix: string
  times: string
  alerts: Record<keyof AlertThresholds, string>
  /** Appointment type key → its Koalendar page, as typed. */
  koalendar: Record<string, { url: string; refQuestion: string; phoneQuestion: string; takesCard: boolean }>
  noShowFee: string
  freeCancelHours: string
}

const QUESTION_CODE = /^[A-Za-z0-9_-]{1,40}$/

function toDraft(settings: BackOfficeSettings): Draft {
  return {
    bookingSources: settings.bookingSources,
    noPurchaseReasons: settings.noPurchaseReasons,
    tierColors: settings.tierColors,
    retiredTiers: settings.retiredTiers,
    salesTax: percentFromBps(settings.salesTaxRateBps),
    presets: settings.discountPresetsPct.join(', '),
    maxDiscount: String(settings.maxDiscountPct),
    orderPrefix: settings.orderNumberPrefix,
    times: settings.appointmentTimes.map(formatWallTime).join(', '),
    alerts: Object.fromEntries(Object.entries(settings.alerts).map(([k, v]) => [k, String(v)])) as Draft['alerts'],
    koalendar: Object.fromEntries(
      Object.entries(settings.booking.koalendar).map(([key, page]) => [
        key,
        { url: page.url, refQuestion: page.refQuestion ?? '', phoneQuestion: page.phoneQuestion ?? '', takesCard: page.takesCard === true },
      ]),
    ),
    noShowFee: (settings.booking.noShowFeeMinor / 100).toFixed(2),
    freeCancelHours: String(settings.booking.freeCancelHours),
  }
}

/** The draft as the API wants it, or the first thing about it a person has to fix. */
function fromDraft(draft: Draft): { ok: true; settings: BackOfficeSettings } | { ok: false; problem: string } {
  const tax = bpsFromPercent(draft.salesTax)
  if (tax === undefined || tax === null) return { ok: false, problem: 'Enter the sales tax as a percentage, like 9.75.' }
  const presets = percentList(draft.presets)
  if (!presets) return { ok: false, problem: 'Discount presets are percentages separated by commas, like 5, 10, 15.' }
  const cap = Number(draft.maxDiscount)
  if (draft.maxDiscount.trim() === '' || !Number.isFinite(cap) || cap < 0 || cap > 100) {
    return { ok: false, problem: 'The discount cap is a percentage from 0 to 100.' }
  }
  const orderPrefix = draft.orderPrefix.trim().toUpperCase()
  if (!/^[A-Z0-9]{1,8}$/.test(orderPrefix)) {
    return { ok: false, problem: 'The order number prefix is 1 to 8 letters or digits, like VH.' }
  }
  const appointmentTimes = timeList(draft.times)
  if (!appointmentTimes) {
    return { ok: false, problem: 'Appointment times are start times separated by commas, like 10:00 AM, 11:30 AM, 2:00 PM.' }
  }
  const alerts = {} as AlertThresholds
  for (const [key, label] of THRESHOLDS) {
    const days = Number(draft.alerts[key])
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      return { ok: false, problem: `"${label}" needs a whole number of days from 1 to 365.` }
    }
    alerts[key] = days
  }
  const koalendar: Record<string, KoalendarPage> = {}
  for (const [key, page] of Object.entries(draft.koalendar)) {
    const url = page.url.trim()
    if (!url) continue
    if (!/^https:\/\/\S+$/.test(url)) return { ok: false, problem: 'Each Koalendar page is its full address, starting with https://.' }
    for (const code of [page.refQuestion.trim(), page.phoneQuestion.trim()]) {
      if (code && !QUESTION_CODE.test(code)) return { ok: false, problem: `"${code}" isn’t a Koalendar question code: letters, digits, - and _ only.` }
    }
    koalendar[key] = {
      url,
      ...(page.refQuestion.trim() ? { refQuestion: page.refQuestion.trim() } : {}),
      ...(page.phoneQuestion.trim() ? { phoneQuestion: page.phoneQuestion.trim() } : {}),
      ...(page.takesCard ? { takesCard: true } : {}),
    }
  }
  const fee = Number(draft.noShowFee.replace(/[$,\s]/g, ''))
  if (draft.noShowFee.trim() === '' || !Number.isFinite(fee) || fee < 0 || fee > 1000) {
    return { ok: false, problem: 'The no-show fee is an amount in dollars from 0 to 1,000. 0 takes no card.' }
  }
  const hours = Number(draft.freeCancelHours)
  if (!Number.isInteger(hours) || hours < 0 || hours > 168) {
    return { ok: false, problem: 'Free cancellation is a whole number of hours from 0 to 168.' }
  }
  return {
    ok: true,
    settings: {
      bookingSources: draft.bookingSources,
      noPurchaseReasons: draft.noPurchaseReasons,
      tierColors: draft.tierColors,
      retiredTiers: draft.retiredTiers,
      salesTaxRateBps: tax,
      discountPresetsPct: presets,
      maxDiscountPct: cap,
      orderNumberPrefix: orderPrefix,
      appointmentTimes,
      alerts,
      booking: { koalendar, noShowFeeMinor: Math.round(fee * 100), freeCancelHours: hours },
    },
  }
}

/** Why a delivery failed, said so the owner knows what to change. */
function failureReason(delivery: FailedKoalendarDelivery): string {
  if (/no appointment type matches/i.test(delivery.error)) {
    return `No appointment type has the Koalendar page ${delivery.page ? `"${delivery.page}"` : 'it was booked on'}. Enter that page above, save, then try again.`
  }
  if (/no booking id|no start time/i.test(delivery.error)) return 'Koalendar sent it without the booking’s details, so there’s nothing to try again.'
  return delivery.error
}

/**
 * Koalendar bookings that reached us and couldn't be made into appointments
 * — a page not entered above, most often — with a button to try them again
 * once that is fixed. Nothing shows when there are none.
 */
function FailedDeliveries() {
  const { token } = useStaffToken()
  const failed = useFailedKoalendar(token)
  const retry = useRetryFailedKoalendar(token)
  const rows = failed.data?.failed ?? []
  const outcome = retry.data
  if (rows.length === 0 && !outcome) return null
  return (
    <div className="mb-6 max-w-4xl rounded border border-[var(--st-alert,#b42318)] p-4" role="region" aria-label="Koalendar bookings that didn’t come through">
      {rows.length > 0 ? (
        <>
          <p className="st-alert mb-2 text-sm font-medium">
            {rows.length === 1 ? 'One Koalendar booking didn’t come through.' : `${rows.length} Koalendar bookings didn’t come through.`}
          </p>
          <ul className="mb-3 flex flex-col gap-2 text-sm">
            {rows.map((row) => (
              <li key={row.id}>
                <span className="tabular-nums">
                  {row.startsAt ? `For ${boutiqueDateTime(row.startsAt)}` : 'No appointment time'}
                </span>
                <span className="st-meta"> · received {boutiqueDateTime(row.receivedAt)}</span>
                <div className="st-meta text-xs">{failureReason(row)}</div>
              </li>
            ))}
          </ul>
          <button type="button" className="st-btn" disabled={retry.isPending} onClick={() => retry.mutate()}>
            {retry.isPending ? 'Trying again…' : 'Try again'}
          </button>
        </>
      ) : null}
      {outcome ? (
        <p className={rows.length === 0 ? 'st-ok text-sm' : 'st-meta mt-2 text-sm'} role="status">
          {outcome.fixed === outcome.tried
            ? outcome.tried === 1
              ? 'It went through: the appointment is on the calendar.'
              : `All ${outcome.tried} went through: the appointments are on the calendar.`
            : `${outcome.fixed} of ${outcome.tried} went through.`}
        </p>
      ) : null}
      {retry.isError ? <p className="st-alert mt-2 text-sm">That didn&rsquo;t work: {retry.error.message}</p> : null}
    </div>
  )
}

/**
 * A link somebody pastes elsewhere — the website's button, Koalendar's
 * redirect — shown in full, with a button that copies it. The text selects
 * whole on a click, so copying by hand works where the clipboard is refused.
 */
function CopyableLink({ label, url }: { readonly label: string; readonly url: string }) {
  const [copied, setCopied] = useState(false)
  const text = useRef<HTMLElement>(null)
  const copy = async () => {
    let done = false
    try {
      await navigator.clipboard.writeText(url)
      done = true
    } catch {
      // The clipboard API refused: the older way, from the text itself, selected.
      const node = text.current
      const selection = window.getSelection()
      if (node && selection) {
        const range = document.createRange()
        range.selectNodeContents(node)
        selection.removeAllRanges()
        selection.addRange(range)
        try {
          done = document.execCommand('copy')
        } catch {
          done = false
        }
      }
    }
    setCopied(done)
    if (done) setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="flex items-center gap-2">
      <span className="st-eyebrow shrink-0">{label}</span>
      <code ref={text} className="min-w-0 flex-1 select-all text-xs break-all" aria-label={label}>
        {url}
      </code>
      <button type="button" className="st-btn-ghost shrink-0 px-2 text-xs" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/**
 * The back office's settings, as one document with one Save: appointment
 * sources, no-purchase reasons, appointment type colors, tax and discounts,
 * and the alert thresholds. Ported from tperent's settings screen — sections
 * down one page, each saying what it changes before asking for a number.
 */
export function BackOfficeSection({ tiers }: { readonly tiers: BookingSettings['tiers'] }) {
  const { token } = useStaffToken()
  const query = useBackOffice(token)
  const save = useSaveBackOffice(token)
  const walkIn = useAppointmentTypes(token).data?.types.find((type) => type.walkIn)?.key
  const [draft, setDraft] = useState<Draft | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (query.data) setDraft(toDraft(query.data))
  }, [query.data])

  if (query.isError) return <p className="st-alert">Could not load the back-office settings. Try again in a moment.</p>
  if (!draft) return <p className="st-meta">Loading…</p>

  const change = (patch: Partial<Draft>) => {
    setDraft({ ...draft, ...patch })
    setProblem(null)
  }

  const submit = () => {
    const built = fromDraft(draft)
    if (!built.ok) return setProblem(built.problem)
    save.mutate(built.settings)
  }

  const saveBar = (
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="st-btn" disabled={save.isPending} onClick={submit}>
        {save.isPending ? 'Saving…' : 'Save back-office settings'}
      </button>
      {problem ? (
        <span className="st-alert" role="alert">
          {problem}
        </span>
      ) : save.isError ? (
        <span className="st-alert" role="alert">
          That didn&rsquo;t save: {save.error.message}
        </span>
      ) : save.isSuccess ? (
        <span className="st-ok">Saved.</span>
      ) : null}
    </div>
  )

  return (
    <div className="mb-10">
      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Appointment sources</h2>
        <p className="st-meta mb-4 max-w-2xl">
          Where an appointment came from, picked when it&rsquo;s booked. Renaming one relabels every appointment that
          already chose it.
        </p>
        <KeyedListEditor
          entries={draft.bookingSources}
          noun="source"
          onChange={(bookingSources) => change({ bookingSources })}
        />
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">No-purchase reasons</h2>
        <p className="st-meta mb-4 max-w-2xl">
          Why the customer didn&rsquo;t buy, recorded on the appointment for the follow-up.
        </p>
        <KeyedListEditor
          entries={draft.noPurchaseReasons}
          noun="reason"
          onChange={(noPurchaseReasons) => change({ noPurchaseReasons })}
        />
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Appointment types</h2>
        <p className="st-meta mb-4 max-w-2xl">
          How each type of appointment is colored on the calendar, and whether it&rsquo;s still offered. A type you stop
          offering can&rsquo;t be booked anymore, online or here, and the appointments already booked as it keep its name.
        </p>
        <table className="st-table max-w-2xl">
          <tbody>
            {tiers.map((tier) => {
              const color = draft.tierColors[tier.key]
              const offered = !draft.retiredTiers.includes(tier.key)
              return (
                <tr key={tier.key} data-tier={tier.key}>
                  <td className={offered ? undefined : 'st-meta'}>{tier.label}</td>
                  <td className="w-28">
                    <label
                      className="flex items-center gap-1.5 text-sm"
                      title={tier.key === walkIn ? 'The walk-in type: a fitting room started at the counter books it, so it’s always offered.' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={offered}
                        disabled={tier.key === walkIn && offered}
                        aria-label={`Offer ${tier.label}`}
                        onChange={(event) =>
                          change({
                            retiredTiers: event.target.checked
                              ? draft.retiredTiers.filter((key) => key !== tier.key)
                              : [...draft.retiredTiers, tier.key],
                          })
                        }
                      />
                      Offered
                    </label>
                  </td>
                  <td className="w-40">
                    <input
                      type="color"
                      aria-label={`Calendar color for ${tier.label}`}
                      value={color ?? DEFAULT_SWATCH}
                      onChange={(event) => change({ tierColors: { ...draft.tierColors, [tier.key]: event.target.value } })}
                    />
                    <span className="st-meta ml-2 text-xs">{color ?? 'Default'}</span>
                  </td>
                  <td className="w-32 text-right">
                    {color ? (
                      <button
                        type="button"
                        className="st-btn-quiet"
                        onClick={() => {
                          const { [tier.key]: _cleared, ...rest } = draft.tierColors
                          change({ tierColors: rest })
                        }}
                      >
                        Use default
                      </button>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Tax and discounts</h2>
        <p className="st-meta mb-4 max-w-2xl">
          Sales tax is added to every order automatically. Each order keeps the rate it was written at, so a change
          here applies to new orders only. Discounts are entered per line; the cap applies to a percentage and to an
          amount alike.
        </p>
        <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">Sales tax %</span>
            <input
              className="st-input"
              inputMode="decimal"
              value={draft.salesTax}
              onChange={(event) => change({ salesTax: event.target.value })}
            />
            <span className="st-meta text-xs">9.75 is Los Angeles County.</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">Discount presets %</span>
            <input
              className="st-input"
              placeholder="5, 10, 15, 20"
              value={draft.presets}
              onChange={(event) => change({ presets: event.target.value })}
            />
            <span className="st-meta text-xs">The quick picks on a line, separated by commas.</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">Largest discount %</span>
            <input
              className="st-input"
              inputMode="decimal"
              value={draft.maxDiscount}
              onChange={(event) => change({ maxDiscount: event.target.value })}
            />
            <span className="st-meta text-xs">Of a line&rsquo;s price, before tax.</span>
          </label>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Appointment times</h2>
        <p className="st-meta mb-4 max-w-2xl">
          The start times offered with one tap when someone books or moves an appointment here. Customers book in
          Koalendar, so this is only for the times the team uses at the counter; any other time can still be picked.
        </p>
        <label className="flex max-w-2xl flex-col gap-1">
          <span className="st-eyebrow">Usual start times</span>
          <input
            className="st-input"
            placeholder="10:00 AM, 11:00 AM, 12:00 PM"
            value={draft.times}
            onChange={(event) => change({ times: event.target.value })}
          />
          <span className="st-meta text-xs">Separated by commas. 2pm, 2:00 PM and 14:00 all work.</span>
        </label>
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Order numbers</h2>
        <p className="st-meta mb-4 max-w-2xl">
          What every order number starts with. Changing it changes new orders only; the numbers carry on from where
          they are, and orders already written keep theirs.
        </p>
        <label className="flex max-w-xs flex-col gap-1">
          <span className="st-eyebrow">Prefix</span>
          <input
            className="st-input uppercase"
            maxLength={8}
            value={draft.orderPrefix}
            onChange={(event) => change({ orderPrefix: event.target.value })}
          />
          <span className="st-meta text-xs tabular-nums">Order numbers read like {draft.orderPrefix.trim().toUpperCase() || 'VH'}-1001.</span>
        </label>
      </section>

      <section className="mb-8">
        <h2 className="st-display mb-1 text-xl">Online booking</h2>
        <p className="st-meta mb-2 max-w-2xl">
          The Koalendar booking page for each appointment type. A type with a page books online: the customer picks a
          time on Koalendar &mdash; after agreeing to the no-show policy and saving a card on Stripe&rsquo;s page, when
          its <strong>Card first</strong> box is ticked (Stripe connected, and a fee above 0). A
          type without one books in the fitting room as before. On each Koalendar page, add a <strong>hidden</strong>{' '}
          question and type its code here, so a booking comes back tied to the customer&rsquo;s fitting room.
        </p>
        <p className="st-meta mb-4 max-w-2xl">
          Once a page is saved, link the website&rsquo;s booking button to the type&rsquo;s website link below. On
          Koalendar, set each page&rsquo;s redirect after booking to the address below, with event details off.
        </p>
        <div className="mb-4 max-w-4xl">
          <CopyableLink label="Redirect after booking" url={`${window.location.origin}/booked`} />
        </div>
        <table className="st-table mb-4 max-w-4xl">
          <thead>
            <tr>
              <th>Appointment type</th>
              <th className="min-w-[24rem]">Koalendar page</th>
              <th className="w-32">Hidden question</th>
              <th className="w-32">Phone question</th>
              <th className="w-24">Card first</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => {
              const page = draft.koalendar[tier.key] ?? { url: '', refQuestion: '', phoneQuestion: '', takesCard: false }
              const set = (patch: Partial<typeof page>) => change({ koalendar: { ...draft.koalendar, [tier.key]: { ...page, ...patch } } })
              return (
                <tr key={tier.key}>
                  <td>{tier.label}</td>
                  <td>
                    <input
                      className="st-input w-full"
                      aria-label={`Koalendar page for ${tier.label}`}
                      placeholder="https://koalendar.com/e/…"
                      value={page.url}
                      onChange={(event) => set({ url: event.target.value })}
                    />
                    {query.data?.booking.koalendar[tier.key] ? (
                      <div className="mt-1.5">
                        <CopyableLink label="Website link" url={`${window.location.origin}/go/${tier.key}`} />
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <input
                      className="st-input"
                      aria-label={`Hidden question code for ${tier.label}`}
                      placeholder="Code"
                      value={page.refQuestion}
                      onChange={(event) => set({ refQuestion: event.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="st-input"
                      aria-label={`Phone question code for ${tier.label}`}
                      value={page.phoneQuestion}
                      onChange={(event) => set({ phoneQuestion: event.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      aria-label={`Save a card before booking ${tier.label}`}
                      checked={page.takesCard}
                      onChange={(event) => set({ takesCard: event.target.checked })}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <FailedDeliveries />
        <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">No-show fee $</span>
            <input className="st-input" inputMode="decimal" value={draft.noShowFee} onChange={(event) => change({ noShowFee: event.target.value })} />
            <span className="st-meta text-xs">Charged to the card on file only when a stylist presses it. 0 takes no card.</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="st-eyebrow">Free cancellation, hours ahead</span>
            <input
              className="st-input"
              inputMode="numeric"
              value={draft.freeCancelHours}
              onChange={(event) => change({ freeCancelHours: event.target.value })}
            />
            <span className="st-meta text-xs">A cancel at least this far ahead costs nothing.</span>
          </label>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="st-display mb-1 text-xl">Alert thresholds</h2>
        <p className="st-meta mb-4 max-w-2xl">
          When an order shows up in Alerts, under To do. Every threshold is in days.
        </p>
        <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
          {THRESHOLDS.map(([key, label, help]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="st-eyebrow">{label}</span>
              <input
                className="st-input"
                inputMode="numeric"
                value={draft.alerts[key]}
                onChange={(event) => change({ alerts: { ...draft.alerts, [key]: event.target.value } })}
              />
              <span className="st-meta text-xs">{help}</span>
            </label>
          ))}
        </div>
      </section>

      {saveBar}
    </div>
  )
}

/**
 * An editable pick list: rename in place, reorder, remove, add.
 *
 * The key of an existing entry never changes here, whatever its label becomes
 * — it is what the appointments that chose it store. A new entry's key is made
 * from its label once, when it is added (`keyForLabel`).
 */
function KeyedListEditor({
  entries,
  noun,
  onChange,
}: {
  readonly entries: readonly KeyedLabel[]
  readonly noun: string
  readonly onChange: (next: KeyedLabel[]) => void
}) {
  const [adding, setAdding] = useState('')

  const move = (index: number, by: -1 | 1) => {
    const next = [...entries]
    const [entry] = next.splice(index, 1)
    if (entry) next.splice(index + by, 0, entry)
    onChange(next)
  }

  const add = () => {
    const label = adding.trim()
    if (!label) return
    onChange([...entries, { key: keyForLabel(label, entries.map((e) => e.key)), label }])
    setAdding('')
  }

  return (
    <div className="max-w-xl">
      <ul className="mb-3 flex flex-col gap-1.5">
        {entries.map((entry, index) => (
          <li key={entry.key} className="flex items-center gap-2">
            <input
              className="st-input flex-1"
              aria-label={`${noun} ${index + 1}`}
              value={entry.label}
              onChange={(event) =>
                onChange(entries.map((e, i) => (i === index ? { ...e, label: event.target.value } : e)))
              }
            />
            <button
              type="button"
              className="st-btn-quiet"
              aria-label={`Move ${entry.label} up`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="st-btn-quiet"
              aria-label={`Move ${entry.label} down`}
              disabled={index === entries.length - 1}
              onClick={() => move(index, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className="st-btn-quiet"
              onClick={() => onChange(entries.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <input
          className="st-input flex-1"
          placeholder={`Add a ${noun}`}
          aria-label={`New ${noun}`}
          value={adding}
          onChange={(event) => setAdding(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              add()
            }
          }}
        />
        <button type="button" className="st-btn-ghost" disabled={!adding.trim()} onClick={add}>
          Add
        </button>
      </div>
    </div>
  )
}
